import React, { useEffect, useState, useRef, useCallback } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import "../styles/allProviders.css"; // Provider-specific CSS
import { SERVER_URL } from "../config";
import ProviderById from "./ProviderById";

interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  specialty?: string;
  licenseNumber: string;
  createdAt: string;
  user: {
    email: string;
    role?: string;
  };
}

const AllProviders: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // New state to store the provider selected for detailed view
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const limit = 20; // Items per page

  // Fetch providers with pagination
  const fetchProviders = useCallback(async () => {
    if (!hasMore) return;
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${SERVER_URL}/api/provider/providers?page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch providers.");
      }
      const data: Provider[] = await response.json();
      // If data returned is less than limit, set hasMore to false.
      if (data.length < limit) {
        setHasMore(false);
      }
      if (data.length > 0) {
        setProviders((prev) => {
          const uniqueProviders = new Map(
            [...prev, ...data].map((p) => [p.id, p])
          );
          return Array.from(uniqueProviders.values());
        });
      }
    } catch (err) {
      console.error("Error fetching provider list:", err);
      setError("Failed to fetch provider data.");
    } finally {
      setLoading(false);
    }
  }, [page, hasMore]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Intersection Observer to load more providers when last item comes into view
  const lastProviderRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Filter providers based on the search term
  const filteredProviders = providers.filter((provider) =>
    `${provider.firstName} ${provider.lastName} ${provider.user.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  // If a provider has been selected, render the ProviderById component
  if (selectedProvider) {
    return (
      <div className="provider-detail-container">
        <button
          className="back-button"
          onClick={() => setSelectedProvider(null)}
        >
          Back to Provider List
        </button>
        <ProviderById providerData={selectedProvider} />
      </div>
    );
  }

  // Otherwise, render the list of providers
  return (
    <div className="provider-list-container">
      <h2>Provider List</h2>
      <input
        type="text"
        placeholder="Search providers by name or email"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input"
      />
      {filteredProviders.length === 0 && !loading ? (
        <p className="no-results">No providers found.</p>
      ) : (
        <ul className="provider-list">
          {filteredProviders.map((provider, index) => (
            <li
              key={provider.id}
              ref={
                index === filteredProviders.length - 1 ? lastProviderRef : null
              }
              className="provider-item"
            >
              <button
                className="provider-link"
                onClick={() => setSelectedProvider(provider)}
              >
                <strong>
                  {provider.firstName} {provider.lastName}
                </strong>{" "}
                - {provider.user.email}
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && <p className="loading-text">Loading providers...</p>}
    </div>
  );
};

export default AllProviders;
